import { useEffect } from 'react';

/**
 * Register a callback on changes to the dimensions
 * of an element's content or border box.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *
 * @param callback Function called back when a resize occurs.
 * Ensure that the function reference is not recreated each render.
 * @param ref Reference to an HTML element to retrieve dimensions from.
 * This hook cannot detect updates to ref, so ensure that the callback
 * reference is updated to re-set the observer.
 */
export const useResizeObserver = (
  callback: (offsets: { width: number; height: number }) => void,
  ref: React.RefObject<HTMLElement>
) => {
  useEffect(() => {
    if (!ref?.current) {
      return;
    }

    try {
      const resizeObserver = new ResizeObserver(() => {
        // When a resize occurs
        if (ref.current?.offsetHeight) {
          callback({
            width: ref.current?.offsetWidth,
            height: ref.current?.offsetHeight,
          });
        }
      });

      resizeObserver.observe(ref.current);

      // Clean up the observer
      return () => resizeObserver.disconnect();
    } catch {
      // This will throw in IE11
      // No action is needed here
    }
  }, [callback, ref]);

  return ref;
};
